import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Search, Mail, Building, Shield } from 'lucide-react';

export const TeamPage = () => {
  const { user, isSuperAdmin } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const response = await getUsers({ status: 'Active' });
      setTeam(response.data);
    } catch (error) {
      console.error('Failed to fetch team:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTeam = team.filter(member =>
    member.full_name.toLowerCase().includes(search.toLowerCase()) ||
    member.email.toLowerCase().includes(search.toLowerCase()) ||
    member.department.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'Super Admin': return 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/20';
      case 'Admin': return 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20';
      case 'Coordinator': return 'bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/20';
      default: return 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border border-gray-500/20';
    }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Team</h1>
          <p className="text-muted-foreground mt-1">
            View {isSuperAdmin ? 'all' : 'your department'} team members
          </p>
        </div>
        
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search team members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-team-input"
          />
        </div>
      </div>

      {/* Team Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
        </div>
      ) : filteredTeam.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No team members found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeam.map((member) => (
            <Card 
              key={member.id} 
              className="border-border/50 hover:border-[#F5A623]/50 transition-colors"
              data-testid={`team-member-${member.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-12 w-12 bg-[#F5A623]/20">
                    <AvatarFallback className="text-[#F5A623] font-semibold text-lg">
                      {member.full_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{member.full_name}</h3>
                    <Badge className={`${getRoleColor(member.role)} mt-1`}>{member.role}</Badge>
                    
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{member.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building className="h-4 w-4" />
                        <span>{member.department}</span>
                      </div>
                      {member.manager_name && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Shield className="h-4 w-4" />
                          <span>Reports to: {member.manager_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
